import React from 'react';

export interface PipelineStat {
  value: string | number;
  label: string;
  /** Render the number in the urgent accent (e.g. "closing soon"). */
  urgent?: boolean;
}

interface PipelineStripProps {
  stats: PipelineStat[];
  /** Leading section label (mono). */
  label?: string;
  /** Optional trailing action, e.g. a "View all →" link. */
  action?: React.ReactNode;
  /** 'bar' = single horizontal strip (desktop); 'grid' = 4-up boxed row (mobile). */
  variant?: 'bar' | 'grid';
  className?: string;
}

/**
 * Pipeline strip — one quiet summary row that replaces the six decorative stat
 * cards. Numbers are Roboto Mono; the "closing soon" stat owns the urgent red.
 */
export function PipelineStrip({
  stats,
  label = 'Pipeline',
  action,
  variant = 'bar',
  className = '',
}: PipelineStripProps) {
  if (variant === 'grid') {
    return (
      <div
        className={`flex bg-bw-surface-subtle border border-bw-border rounded-xl px-3 py-2.5 ${className}`}
      >
        {stats.map((s, i) => (
          <div
            key={i}
            className={`flex-1 flex flex-col items-center px-1 ${
              i < stats.length - 1 ? 'border-r border-bw-border' : ''
            }`}
          >
            <span className={`font-mono text-[17px] font-medium ${s.urgent ? 'text-bw-accent' : 'text-bw-ink'}`}>
              {s.value}
            </span>
            <span className="text-[10px] text-bw-muted">{s.label}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-3 bg-bw-surface-subtle border border-bw-border rounded-lg px-3.5 py-2.5 ${className}`}
    >
      <span className="hidden sm:inline font-mono text-[10px] font-bold text-bw-muted tracking-[0.1em] uppercase flex-shrink-0">
        {label}
      </span>
      <div className="flex flex-1 items-center min-w-0 overflow-x-auto scrollbar-hide">
        {stats.map((s, i) => (
          <div
            key={i}
            className={`flex items-center gap-1.5 flex-1 min-w-max ${
              i < stats.length - 1 ? 'border-r border-bw-border pr-2.5 mr-2.5' : ''
            }`}
          >
            <span className={`font-mono text-[13px] font-medium ${s.urgent ? 'text-bw-accent' : 'text-bw-ink'}`}>
              {s.value}
            </span>
            <span className="text-[11px] text-bw-muted whitespace-nowrap">{s.label}</span>
          </div>
        ))}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

export default PipelineStrip;
